'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

const COOKIE_KEY = 'rzr_cookie_consent_v1'

type Consent = 'essential' | 'all'

export function CookieBanner() {
  const pathname = usePathname() ?? ''
  const [show, setShow] = useState(false)

  // Backstage is an authenticated ops console — DSGVO cookie consent
  // doesn't apply (no marketing/3rd-party tracking there), and the banner
  // was overlapping the auth form's "break-glass" link.
  const suppressed = pathname.startsWith('/backstage')

  useEffect(() => {
    if (suppressed) return
    try {
      const v = localStorage.getItem(COOKIE_KEY)
      if (!v) setShow(true)
    } catch {
      setShow(true)
    }
  }, [suppressed])

  function accept(level: Consent) {
    try {
      localStorage.setItem(COOKIE_KEY, JSON.stringify({ level, ts: Date.now() }))
    } catch { /* ignore */ }
    setShow(false)
  }

  if (suppressed || !show) return null

  // Mobile: compact pill at top (one-line), expanded only on focus
  // Desktop: keep full banner at bottom
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie-Hinweis"
      className="fixed inset-x-2 bottom-2 z-50 mx-auto max-w-3xl rounded-xl border border-neutral-200 bg-white shadow-2xl md:inset-x-3 md:bottom-3 md:rounded-2xl"
    >
      <div className="flex items-center gap-3 p-3 md:flex-row md:items-center md:justify-between md:p-5 md:gap-4">
        <div className="flex-1 min-w-0 text-[12.5px] text-neutral-700 md:text-sm">
          <p className="font-semibold text-neutral-900 mb-0.5 md:mb-1">🍪 Cookie-Hinweis</p>
          <p className="line-clamp-2 md:line-clamp-none">
            <span className="hidden md:inline">Wir verwenden nur technisch notwendige Cookies (Login, Sitzung, Sicherheit). Keine Werbe-Tracker, keine Drittanbieter-Analyse.{' '}</span>
            <span className="md:hidden">Nur technische Cookies, kein Tracking.{' '}</span>
            <Link href="/datenschutz" className="underline text-brand-700">Datenschutz</Link>.
          </p>
        </div>
        <div className="flex gap-1.5 flex-shrink-0 md:gap-2">
          <button
            type="button"
            onClick={() => accept('essential')}
            className="hidden md:inline-flex rounded-lg border border-neutral-200 px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
          >
            Nur notwendige
          </button>
          <button
            type="button"
            onClick={() => accept('all')}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 transition-colors md:px-4 md:py-2 md:text-sm"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  )
}
